import React, { useState, useEffect, useRef } from 'react';

const APYGroupContact = () => {
  // Estados para gestionar el formulario y las animaciones
  const [isLoaded, setIsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    empresa: '',
    consulta: ''
  });
  const [formFocus, setFormFocus] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [isVisible, setIsVisible] = useState({
    hero: false,
    contactForm: false,
    contactInfo: false
  });
  
  // Referencias para secciones
  const heroRef = useRef(null);
  const contactFormRef = useRef(null);
  const contactInfoRef = useRef(null);
  
  // Validar formulario
  useEffect(() => {
    const { nombre, email, consulta } = formData;
    setFormValid(nombre.trim() !== '' && email.trim() !== '' && consulta.trim() !== '');
  }, [formData]);

  // Simulación de carga inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Observación de secciones para animaciones
  useEffect(() => {
    const observerOptions = {
      threshold: 0.25,
      rootMargin: "0px 0px -100px 0px"
    };
    
    const handleIntersect = (entries) => {
      entries.forEach(entry => {
        const { id } = entry.target;
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, [id]: true }));
        }
      });
    };
    
    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    
    const refs = [
      { ref: heroRef, id: 'hero' },
      { ref: contactFormRef, id: 'contactForm' },
      { ref: contactInfoRef, id: 'contactInfo' }
    ];
    
    refs.forEach(({ ref, id }) => {
      if (ref.current) {
        ref.current.id = id;
        observer.observe(ref.current);
      }
    });
    
    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, []);

  // Manejador de cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Manejador de focus en campos del formulario
  const handleFocus = (field) => {
    setFormFocus(field);
  };

  // Manejador de blur en campos del formulario
  const handleBlur = () => {
    setFormFocus(null);
  };

  // Manejador de envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    if (formValid) {
      setFormSubmitted(true);
      // Aquí iría la lógica de envío real
      setTimeout(() => {
        // Simular respuesta
        setFormData({
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          empresa: '',
          consulta: ''
        });
        setFormSubmitted(false);
      }, 2000);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-[40vh] flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/70 z-10"></div>
          <img 
            src="" 
            alt="" 
            className="w-full h-full object-cover filter grayscale"
          />
        </div>
        
        <div className={`relative z-10 container mx-auto px-6 text-center transform transition-all duration-1000 ease-out ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <h1 className="text-7xl font-black tracking-widest mb-6">
            {['C','O','N','T','A','C','T','O'].map((letter, index) => (
              <span 
                key={index}
                className="inline-block transition-all duration-300 hover:text-red-600"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                {letter}
              </span>
            ))}
          </h1>
          <div className="w-20 h-1 bg-red-600 mx-auto mt-4"></div>
        </div>
      </section>

      {/* Form Section */}
      <section 
        ref={contactFormRef}
        className="py-20 relative z-10"
      >
        <div className="absolute top-0 right-0 w-1/3 h-96 bg-red-600/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-80 bg-red-600/5 rounded-full filter blur-3xl"></div>
        
        <div className="container mx-auto max-w-4xl px-6">
          {formSubmitted ? (
            <div className="text-center py-16 animate-fadeIn">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-4">Mensaje Enviado</h2>
              <p className="text-gray-400 max-w-md mx-auto">Gracias por contactarnos. Nuestro equipo responderá a tu consulta lo antes posible.</p>
              <button 
                onClick={() => setFormSubmitted(false)}
                className="mt-8 px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
              >
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <form 
              onSubmit={handleSubmit}
              className={`transition-all duration-1000 ${
                isVisible.contactForm ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="relative">
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    onFocus={() => handleFocus('nombre')}
                    onBlur={handleBlur}
                    className={`w-full bg-transparent border-b-2 ${
                      formFocus === 'nombre' ? 'border-red-600' : 'border-gray-700'
                    } px-2 py-3 focus:outline-none transition-colors`}
                    placeholder="NOMBRE"
                    required
                  />
                  <div className={`absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 ${
                    formFocus === 'nombre' ? 'w-full' : 'w-0'
                  }`}></div>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    name="apellido"
                    value={formData.apellido}
                    onChange={handleChange}
                    onFocus={() => handleFocus('apellido')}
                    onBlur={handleBlur}
                    className={`w-full bg-transparent border-b-2 ${
                      formFocus === 'apellido' ? 'border-red-600' : 'border-gray-700'
                    } px-2 py-3 focus:outline-none transition-colors`}
                    placeholder="APELLIDO"
                  />
                  <div className={`absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 ${
                    formFocus === 'apellido' ? 'w-full' : 'w-0'
                  }`}></div>
                </div>
              </div>
              
              <div className="relative mb-6">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => handleFocus('email')}
                  onBlur={handleBlur}
                  className={`w-full bg-transparent border-b-2 ${
                    formFocus === 'email' ? 'border-red-600' : 'border-gray-700'
                  } px-2 py-3 focus:outline-none transition-colors`}
                  placeholder="E-MAIL"
                  required
                />
                <div className={`absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 ${
                  formFocus === 'email' ? 'w-full' : 'w-0'
                }`}></div>
              </div>
              
              <div className="relative mb-6">
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  onFocus={() => handleFocus('telefono')}
                  onBlur={handleBlur}
                  className={`w-full bg-transparent border-b-2 ${
                    formFocus === 'telefono' ? 'border-red-600' : 'border-gray-700'
                  } px-2 py-3 focus:outline-none transition-colors`}
                  placeholder="TELÉFONO"
                />
                <div className={`absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 ${
                  formFocus === 'telefono' ? 'w-full' : 'w-0'
                }`}></div>
              </div>
              
              <div className="relative mb-6">
                <input
                  type="text"
                  name="empresa"
                  value={formData.empresa}
                  onChange={handleChange}
                  onFocus={() => handleFocus('empresa')}
                  onBlur={handleBlur}
                  className={`w-full bg-transparent border-b-2 ${
                    formFocus === 'empresa' ? 'border-red-600' : 'border-gray-700'
                  } px-2 py-3 focus:outline-none transition-colors`}
                  placeholder="EMPRESA"
                />
                <div className={`absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 ${
                  formFocus === 'empresa' ? 'w-full' : 'w-0'
                }`}></div>
              </div>
              
              <div className="relative mb-10">
                <textarea
                  name="consulta"
                  value={formData.consulta}
                  onChange={handleChange}
                  onFocus={() => handleFocus('consulta')}
                  onBlur={handleBlur}
                  className={`w-full bg-transparent border-b-2 ${
                    formFocus === 'consulta' ? 'border-red-600' : 'border-gray-700'
                  } px-2 py-3 h-32 resize-none focus:outline-none transition-colors`}
                  placeholder="CONSULTA"
                  required
                ></textarea>
                <div className={`absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 ${
                  formFocus === 'consulta' ? 'w-full' : 'w-0'
                }`}></div>
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={!formValid}
                  className={`px-12 py-3 rounded-full text-white font-semibold transition-all duration-300 relative overflow-hidden ${
                    formValid ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-800 cursor-not-allowed'
                  }`}
                >
                  <span className="relative z-10">ENVIAR</span>
                  {formValid && (
                    <span className="absolute inset-0 w-0 bg-white/20 transition-all duration-300 group-hover:w-full"></span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
      
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default APYGroupContact;